/** A calendar day or a calendar month, in the browser's local time. */
export type AccountingSpan = "day" | "month";

export interface AccountingRangeBounds {
  from: Date;
  to: Date;
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/** `YYYY-MM-DD` for a date input, in local time. */
export function localDateInputValue(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** `YYYY-MM` for a month input, in local time. */
export function localMonthInputValue(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
}

/**
 * Half-open local midnight range for a `YYYY-MM-DD` day. Undefined when the day is not a
 * real calendar day, so February 31 never becomes March.
 */
export function localDayRange(day: string): AccountingRangeBounds | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return undefined;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const date = Number(match[3]);
  const from = new Date(year, month - 1, date);
  if (from.getFullYear() !== year || from.getMonth() !== month - 1 || from.getDate() !== date) {
    return undefined;
  }
  return { from, to: new Date(year, month - 1, date + 1) };
}

/** Half-open local range for a `YYYY-MM` month. Undefined when the month is not 01–12. */
export function localMonthRange(monthValue: string): AccountingRangeBounds | undefined {
  const match = /^(\d{4})-(\d{2})$/.exec(monthValue);
  if (!match) return undefined;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) return undefined;
  return { from: new Date(year, month - 1, 1), to: new Date(year, month, 1) };
}
